import { events } from "@constants/tracking";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { getVerifyUiState } from "@features/auth/helpers/verify";
import { useMagicLinkTokens } from "@features/auth/hooks/useMagicLinkTokens";
import { usePollUserPublicMetadata } from "@features/auth/hooks/usePollUserPublicMetadata";
import { usePostAuthRedirectPath } from "@features/auth/hooks/usePostAuthRedirectPath";
import { AppBar } from "@features/dashboard/layouts/AppBar";
import { buildType } from "@helpers/app-identity";
import { useAppVersion } from "@helpers/app-version";
import { handleNavigateBack } from "@helpers/common";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { useAppStore } from "@hooks/useAppStore";
import useClerkAuth from "@hooks/useClerkAuth";
import { formatError } from "@hooks/useClerkAuth/helpers";
import { isSignUpError } from "@hooks/useClerkAuth/helpers";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { isSafeUrl } from "@memorang/helpers/common";
import { composeMessage } from "@memorang/helpers/email";
import { isWeb } from "@memorang/helpers/platform";
import { AuthResendButton } from "@memorang/ui/components/Auth/AuthResendButton";
import ContactSupport from "@memorang/ui/components/Auth/ContactSupport";
import VerificationLinkExpired from "@memorang/ui/components/Auth/VerificationLinkExpired";
import { VerificationSuccessView } from "@memorang/ui/components/Auth/VerificationSuccessView";
import Box from "@memorang/ui/components/Box";
import Button from "@memorang/ui/components/Button";
import Loader from "@memorang/ui/components/Loader";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import { useToast } from "@memorang/ui/components/Toast";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useDevice } from "@memorang/ui/useDevice";
import Icon from "@react-native-vector-icons/material-design-icons/static";
import { trackCustomEvent } from "analytics";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import DocumentTitle from "components/DocumentTitle";
import * as Device from "expo-device";
import { Redirect, Stack, useLocalSearchParams, useRouter } from "expo-router";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import { openInbox } from "react-native-email-link";
import { Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const RESEND_EMAIL_TIMEOUT = 90;

const VerifyAppbar = memo(
	({
		isLinkExpired,
	}: {
		isLinkExpired: boolean;
	}) => {
		const { t } = useTranslation();
		const { isMobile } = useDevice();

		return (
			<AppBar
				variant="public"
				title={isLinkExpired ? "" : t("auth.verify.verifyAccountTitle")}
				showLogo={!isMobile}
				onPressBack={() => handleNavigateBack("/")}
			/>
		);
	},
	(prevProps, nextProps) => {
		return prevProps.isLinkExpired === nextProps.isLinkExpired;
	},
);

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
const VerifyRoute = () => {
	const router = useRouter();
	const params = useLocalSearchParams<{ email: string }>();

	const theme = useAppTheme();
	const insets = useSafeAreaInsets();
	const { showToast } = useToast();
	const { t } = useTranslation();
	const { isMobile } = useDevice();

	const appId = useAppStore((state) => state.app.id);
	const appName = useAppStore((state) => state.app.name);
	const appScheme = useAppStore((state) => state.app.scheme);
	const tenantId = useAppStore((state) => state.tenant.id);
	const baseUrl = useAppStore((state) => state.app.links.baseUrl);
	const supportEmail = useAppStore((state) => state.supportEmail);
	const appVersion = useAppVersion();
	const setLastLoggedInEmail = useGlobalStore(
		(state) => state.setLastLoggedInEmail,
	);

	const { isSignedIn } = useAuthContext();
	const { deepLinkUrl, authMode, os, status, hasSessionInUrl } =
		useMagicLinkTokens();
	const { isLoading: isOnboardingLoading, postAuthRedirectPath } =
		usePostAuthRedirectPath();

	const [resendResetKey, setResendResetKey] = useState(0);

	const { authenticate, isAuthInProgress } = useClerkAuth({
		appId,
		tenantId,
		redirectUrl: `${baseUrl}/verify`,
		onEmailLinkVerify: (_) => {
			setResendResetKey((key) => key + 1);
			showToast({
				message: t("auth.verify.emailResent", { email: emailFromParams }),
			});
		},
	});

	const emailFromParams = params.email
		? params.email.replace(/\s/g, "+")
		: undefined;

	const pollQueryKey = useMemo(
		() => ["verify-email", emailFromParams],
		[emailFromParams],
	);
	const { userLogicalId, isWaitingForPublicMetadata, hasTimedOut } =
		usePollUserPublicMetadata({
			queryKey: pollQueryKey,
			enabled: isSignedIn,
		});

	const uiState = getVerifyUiState({
		hasSessionInUrl,
		isWeb,
		isSignedIn,
		userLogicalId,
		status,
		hasTimedOut,
	});
	const isLinkExpired = uiState.type === "expired";

	useEffect(() => {
		if (isSignedIn && userLogicalId) {
			trackCustomEvent({
				eventName:
					authMode === "signup" ? events.userRegistered : events.userLoggedIn,
				userId: userLogicalId,
				email: emailFromParams,
				tenantId,
			});
		}
	}, [isSignedIn, userLogicalId, emailFromParams, tenantId, authMode]);

	useEffect(() => {
		if (emailFromParams) {
			setLastLoggedInEmail(emailFromParams);
		}
	}, [emailFromParams, setLastLoggedInEmail]);

	const handleOpenDeepLink = useCallback(() => {
		if (!(deepLinkUrl && appScheme)) {
			return;
		}
		if (!isSafeUrl(deepLinkUrl, { allowedSchemes: [appScheme, "https"] })) {
			return;
		}
		window.location.href = deepLinkUrl;
	}, [deepLinkUrl, appScheme]);

	useEffect(() => {
		if (isWeb && isMobile && (os === "android" || os === "ios")) {
			handleOpenDeepLink();
		}
	}, [handleOpenDeepLink, isMobile, os]);

	const deviceInfo = `- Device: ${Device.deviceName || Device.DeviceType[Device.deviceType || 0]}
		- Operating system: ${Platform.OS}
		- App version: ${appVersion}
		- Build type: ${buildType}
		- Email: ${emailFromParams}`;

	const handleExpiredLinkSupport = () => {
		composeMessage(
			`${appName} Magic link expired`,
			`I'm getting a verification link that is expired. My device info is as follows:
				${deviceInfo}`,
			supportEmail,
		);
	};

	const handleComposeMessage = () => {
		const subject = `${appName} Magic link not received`;
		const emailBody = `Hi there, I am currently having trouble finding my magic link to login to the ${appName} app. Thank you! My device info is as follows:
		${deviceInfo}
		`;
		composeMessage(subject, emailBody, supportEmail);
	};

	if (
		isOnboardingLoading &&
		(uiState.type === "consuming" || uiState.type === "synced")
	) {
		return <Loader />;
	}

	// Redirect logged in user that followed magic link in this tab (or native, which has no other tab)
	if (uiState.type === "consuming") {
		return <Redirect href={postAuthRedirectPath} />;
	}

	// Show verification success view for logged in user that followed magic link in another tab
	if (uiState.type === "synced") {
		return (
			<>
				<Stack.Screen
					options={{
						headerShown: true,
						header: () => <VerifyAppbar isLinkExpired={isLinkExpired} />,
					}}
				/>
				<DocumentTitle title={t("auth.verify.verifyAccountTitle")} />
				<ScrollContainer
					maxWidth="xs"
					contentContainerStyle={{
						paddingTop: 24,
					}}
				>
					<VerificationSuccessView
						onContinue={() => router.replace(postAuthRedirectPath)}
					/>
				</ScrollContainer>
			</>
		);
	}

	if (uiState.type === "timeout") {
		return (
			<>
				<Stack.Screen
					options={{
						headerShown: true,
						header: () => <VerifyAppbar isLinkExpired={isLinkExpired} />,
					}}
				/>
				<ScrollContainer maxWidth="xs">
					<Box
						id="verify-timeout-view"
						flex={1}
						padding={16}
						justifyContent="center"
						gap={16}
					>
						<Text variant="titleLarge" style={{ textAlign: "center" }}>
							{t("auth.verify.accountSetupTimeoutTitle")}
						</Text>
						<Text style={{ textAlign: "center" }}>
							{t("auth.verify.accountSetupTimeoutBody")}
						</Text>
						<ContactSupport
							label={t("auth.verify.contactSupport")}
							disabled={false}
							email={emailFromParams ?? ""}
							buttonText={t("auth.verify.contactSupportLink")}
							handleOpenSupport={handleComposeMessage}
						/>
						<Button
							testID="verify-timeout-login-button"
							mode="contained"
							onPress={() => {
								router.replace("/");
							}}
							style={{ width: "100%" }}
						>
							{t("auth.verify.loginAgain")}
						</Button>
					</Box>
				</ScrollContainer>
			</>
		);
	}

	const handleResendMagicLink = async () => {
		try {
			if (!emailFromParams) {
				throw new Error("Email is required");
			}
			await authenticate(emailFromParams);
		} catch (error: unknown) {
			const isSignUp = isSignUpError(error);
			const longMessage = formatError(error);
			const errorObj = new Error(
				`Error during resend ${isSignUp ? "sign-up" : "sign-in"} magic link`,
			);
			captureErrorToSentry(errorObj, {
				error: JSON.stringify(longMessage),
			});
			trackCustomEvent({
				eventName: isSignUp ? "mem_signup_attempted" : "mem_login_attempted",
				email: emailFromParams,
			});
		}
	};

	const cta = emailFromParams
		? t("auth.verify.openEmail")
		: authMode === "signup"
			? t("auth.verify.signUpCta")
			: t("auth.verify.signInCta");
	const loading =
		isSignedIn && isWaitingForPublicMetadata ? true : !emailFromParams;
	const disabled =
		isSignedIn && isWaitingForPublicMetadata ? true : !emailFromParams;

	return (
		<>
			<Stack.Screen
				options={{
					headerShown: true,
					header: () => <VerifyAppbar isLinkExpired={isLinkExpired} />,
				}}
			/>
			<DocumentTitle
				title={
					isLinkExpired
						? t("auth.verify.verificationLinkExpired")
						: t("auth.verify.verifyAccountTitle")
				}
			/>
			<ScrollContainer maxWidth="xs">
				{isLinkExpired ? (
					<VerificationLinkExpired
						onSupportContact={handleExpiredLinkSupport}
						onLoginRetry={() => {
							router.replace("/");
						}}
					/>
				) : (
					<Box flexGrow={1} height="100%" padding={16}>
						{isWeb && (os === "android" || os === "ios") ? (
							<Box
								id="verify-deeplink-view"
								flex={1}
								alignItems="center"
								justifyContent="center"
								gap={16}
							>
								<Icon
									name="open-in-app"
									size={120}
									color={theme.colors.primary}
								/>
								<Text variant="titleLarge" style={{ textAlign: "center" }}>
									{t("auth.verify.redirectingToApp", { appName })}
								</Text>

								<Text style={{ textAlign: "center" }}>
									{t("auth.verify.appNotOpen")}
								</Text>
								<Button
									testID="verify-open-app-button"
									mode="contained"
									onPress={handleOpenDeepLink}
									style={{ width: "100%" }}
								>
									{t("auth.verify.openApp", { appName })}
								</Button>
								<Text style={{ textAlign: "center" }} variant="bodySmall">
									{t("auth.verify.installAppPrompt", { appName })}
								</Text>
							</Box>
						) : (
							<Box
								id="verify-waiting-view"
								gap={16}
								justifyContent="space-between"
								flex={1}
							>
								<Box flex={1} justifyContent="center">
									<Box alignItems="center" justifyContent="center" gap={16}>
										<Icon
											name="email-check-outline"
											size={120}
											color={theme.colors.primary}
										/>

										<Text variant="titleLarge" style={{ textAlign: "center" }}>
											{t("auth.verify.confirmEmail")}
										</Text>

										<Box>
											<CustomMarkdownRenderer
												textAlign="center"
												fontSize={16}
												variant="stem"
												customStyles={{
													text: {
														lineHeight: 24,
													},
												}}
												text={
													deepLinkUrl
														? t(
																authMode === "signup"
																	? "auth.verify.signUpBody"
																	: "auth.verify.signInBody",
																{
																	appName,
																},
															)
														: t("auth.verify.magicLinkEmail", {
																email: emailFromParams,
																appName,
															})
												}
											/>
											<Text
												variant="bodyLarge"
												style={{
													textAlign: "center",
													textDecorationLine: "underline",
												}}
											>
												{t("auth.verify.magicLinkEmailExpiry")}
											</Text>
										</Box>
									</Box>
								</Box>

								<Box
									width="100%"
									gap={16}
									paddingBottom={insets.bottom}
									maxWidth={500}
									marginTop={24}
									alignSelf="center"
								>
									{(!isWeb || authMode) && (
										<Button
											testID="verify-open-email-button"
											mode="contained"
											onPress={() => openInbox()}
											disabled={disabled}
											loading={loading}
											style={{ width: "100%" }}
										>
											{cta}
										</Button>
									)}
									{!deepLinkUrl && (
										<>
											<AuthResendButton
												key={resendResetKey}
												initialResendEmailIn={RESEND_EMAIL_TIMEOUT}
												onResend={handleResendMagicLink}
												disabled={disabled || isAuthInProgress}
											/>
											<ContactSupport
												label={t("auth.verify.needHelpLabel")}
												disabled={loading}
												email={emailFromParams ?? ""}
												buttonText={t("auth.verify.contactSupportButton")}
												handleOpenSupport={handleComposeMessage}
											/>
										</>
									)}
								</Box>
							</Box>
						)}
					</Box>
				)}
			</ScrollContainer>
		</>
	);
};

export default VerifyRoute;
